/**
 * safeStorage — defensive wrapper around `localStorage` for non-sensitive UI prefs.
 *
 * Why:
 *   - Corrupted JSON in localStorage must NEVER crash the app on boot.
 *   - SSR / private-browsing / disabled-storage environments must degrade gracefully.
 *   - All sensitive auth/session data lives in httpOnly cookies — this util is for
 *     theme, density, saved-view sort orders, and other client-side cosmetic state.
 *
 * Usage:
 *   import { safeStorage } from '../utils/safeStorage';
 *   const view = safeStorage.getJSON('polkdesq:saved-views', []);
 *   safeStorage.setJSON('polkdesq:saved-views', updated);
 *   safeStorage.remove('polkdesq:saved-views');
 *
 * Direct string accessors are available too (`getString` / `setString`) for cases
 * where the value is already a primitive.
 */
import { logger } from './logger';

const memFallback = new Map();

// Guard: this store is for cosmetic UI prefs ONLY. Auth/session/PII material
// must live in httpOnly cookies — refuse keys that look like secrets so a
// future regression can't silently leak credentials into localStorage.
const SENSITIVE_KEY_PATTERN = /token|secret|password|credential|api[-_]?key|authorization|session[-_]?id/i;

function isSensitiveKey(key) {
  if (SENSITIVE_KEY_PATTERN.test(String(key))) {
    logger.error(`safeStorage: refusing to store potentially sensitive key "${key}". Use httpOnly cookies for auth/session data.`);
    return true;
  }
  return false;
}

function hasStorage() {
  try {
    return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
  } catch (err) {
    logger.warn('safeStorage: localStorage probe threw, falling back to in-memory:', err?.message);
    return false;
  }
}

function readRaw(key) {
  if (hasStorage()) {
    try {
      return window.localStorage.getItem(key);
    } catch (err) {
      // Quota / security / private-mode block — fall through to in-memory.
      logger.warn(`safeStorage.read("${key}") failed, using memory fallback:`, err?.message);
    }
  }
  return memFallback.has(key) ? memFallback.get(key) : null;
}

function writeRaw(key, value) {
  if (isSensitiveKey(key)) return false;
  if (hasStorage()) {
    try {
      window.localStorage.setItem(key, value);
      return true;
    } catch (err) {
      // Quota / disabled — fall through to in-memory so the app keeps working.
      logger.warn(`safeStorage.write("${key}") failed, using memory fallback:`, err?.message);
    }
  }
  memFallback.set(key, value);
  return false;
}

function removeRaw(key) {
  if (hasStorage()) {
    try {
      window.localStorage.removeItem(key);
    } catch (err) {
      logger.warn(`safeStorage.remove("${key}") failed:`, err?.message);
    }
  }
  memFallback.delete(key);
}

export const safeStorage = {
  getString(key, fallback = null) {
    const raw = readRaw(key);
    return raw === null || raw === undefined ? fallback : raw;
  },
  setString(key, value) {
    if (value === null || value === undefined) {
      removeRaw(key);
      return;
    }
    writeRaw(key, String(value));
  },
  getJSON(key, fallback = null) {
    const raw = readRaw(key);
    if (raw === null || raw === undefined) return fallback;
    try {
      const parsed = JSON.parse(raw);
      return parsed === null || parsed === undefined ? fallback : parsed;
    } catch (err) {
      // Corrupted JSON — drop it so it can't poison future reads.
      logger.warn(`safeStorage.getJSON("${key}") found corrupted JSON, dropping:`, err?.message);
      removeRaw(key);
      return fallback;
    }
  },
  setJSON(key, value) {
    if (value === undefined) {
      removeRaw(key);
      return;
    }
    try {
      writeRaw(key, JSON.stringify(value));
    } catch (err) {
      // Circular ref or unserializable value — best-effort, don't crash.
      logger.warn(`safeStorage.setJSON("${key}") could not serialize value:`, err?.message);
    }
  },
  remove(key) {
    removeRaw(key);
  },
  // Escape hatch for code that needs the raw API (e.g. iterating keys).
  // Returns null in non-browser environments.
  raw() {
    return hasStorage() ? window.localStorage : null;
  },
};

export default safeStorage;
